import { useState } from "react";
import { XIcon } from "lucide-react";
import { useSubscription } from "@context/subscription_context";
import { trialGraceDaysRemaining } from "@constant/subscription";
import { useUserProfileContext } from "@context/user_profile";
import { maxTrialUsagePercent } from "@util/subscription-usage";
import { resolveBillingPlatform } from "@util/billing-platform";

const USAGE_THRESHOLD = 80;
const DAYS_THRESHOLD = 3;
const DISMISSED_KEY = "trial_banner_dismissed_date";

export default function TrialUsageBanner() {
    const { subscriptionActive, reminderExpiredDay, openViewPlanDialog } = useSubscription();
    const [, userInfo] = useUserProfileContext();
    const [isDismissed, setIsDismissed] = useState(() => {
        const dismissedDate = localStorage.getItem(DISMISSED_KEY);
        if (!dismissedDate) return false;
        const today = new Date().toISOString().split("T")[0];
        return dismissedDate === today;
    });

    if (!subscriptionActive || !subscriptionActive.is_active) return null;
    // Banner is for trial workspaces only — paid plans shouldn't see the
    // "nearing end of trial" copy.
    if (!subscriptionActive.is_trial) return null;

    // Worst cap utilisation across whichever caps this platform exposes
    // (recruiter CV/AI, or Training session/campaign/AIQM — BE #3215), clamped
    // to 100. Shared with subscription_context so banner + dialog agree.
    const usagePct = Math.min(100, Math.round(maxTrialUsagePercent(subscriptionActive)));
    // Per spec, the right-side metrics are driven by which condition(s)
    // triggered the banner:
    //   1. Usage ≥ 80% only          → "{X}% used"
    //   2. Days ≤ 3 only              → "{N} day(s) remaining"
    //   3. Both triggered             → "{X}% used · {N} day(s) remaining"
    // We only treat `reminderExpiredDay` as meaningful when the workspace
    // actually has an `ended_at`. Otherwise the context defaults it to 0
    // and 0 ≤ DAYS_THRESHOLD would otherwise force the banner to show
    // with "Ends tomorrow" on workspaces that have no expiry date at all.
    const hasMeaningfulRemainingDays =
        !!subscriptionActive.ended_at
        && typeof reminderExpiredDay === "number"
        && Number.isFinite(reminderExpiredDay);
    const usageHigh = usagePct >= USAGE_THRESHOLD;
    const daysLow = hasMeaningfulRemainingDays && reminderExpiredDay <= DAYS_THRESHOLD;
    // Once the trial has expired we always show the banner with the
    // "trial has ended" copy — independent of usage and days thresholds.
    const isExpired = !!subscriptionActive.is_expired;
    // Grace window after the trial's `ended_at`. The countdown is derived from
    // the SAME source-of-truth constant as the SubscriptionProvider's
    // restriction logic (`@constant/subscription`), so "N days left before your
    // jobs are paused" hits 0 exactly when the workspace becomes restricted
    // (#2689).
    let graceDaysRemaining = 0;
    if (isExpired && subscriptionActive.ended_at) {
        const endedAt = new Date(subscriptionActive.ended_at);
        const today = new Date();
        const end = new Date(endedAt.getFullYear(), endedAt.getMonth(), endedAt.getDate());
        const current = new Date(today.getFullYear(), today.getMonth(), today.getDate());
        const daysSinceEnd = Math.floor((current.getTime() - end.getTime()) / (1000 * 3600 * 24));
        graceDaysRemaining = trialGraceDaysRemaining(daysSinceEnd);
    }
    const shouldShow = isExpired || usageHigh || daysLow;

    if (!shouldShow || isDismissed) return null;

    const handleDismiss = () => {
        const today = new Date().toISOString().split("T")[0];
        localStorage.setItem(DISMISSED_KEY, today);
        setIsDismissed(true);
    };

    const dayLabel =
        reminderExpiredDay === 0
            ? "Ends tomorrow"
            : `${reminderExpiredDay} day${reminderExpiredDay === 1 ? "" : "s"} remaining`;

    // Training-agent workspaces get training-worded copy (#2687); recruiter (and
    // any other) workspaces keep the existing recruiter wording.
    const isTraining = resolveBillingPlatform(userInfo) === "training";
    const pausedNoun = isTraining ? "campaigns are paused" : "jobs are paused";
    const uninterrupted = isTraining
        ? "uninterrupted training"
        : "uninterrupted hiring";

    return (
        <div className="bg-blue-600 px-7 py-2.5 flex items-center justify-between">
            <div className="flex items-center gap-2">
                <span className="text-[13px] font-medium text-white">
                    {isExpired ? (
                        graceDaysRemaining > 0 ? (
                            <>
                                Your trial has expired. <strong>{graceDaysRemaining}</strong> day{graceDaysRemaining === 1 ? "" : "s"} left before your {pausedNoun} and workspace is restricted.{" "}
                                <button
                                    onClick={() => openViewPlanDialog()}
                                    className="underline font-semibold hover:opacity-80"
                                >
                                    Get in Touch
                                </button>
                            </>
                        ) : (
                            <>
                                Your trial period has expired, upgrade now for {uninterrupted}.{" "}
                                <button
                                    onClick={() => openViewPlanDialog()}
                                    className="underline font-semibold hover:opacity-80"
                                >
                                    Get in Touch
                                </button>
                            </>
                        )
                    ) : (
                        <>
                                You're nearing the end of your trial workspace. Upgrade for {uninterrupted}.{" "}
                                <button
                                    onClick={() => openViewPlanDialog()}
                                    className="underline font-semibold hover:opacity-80"
                                >
                                    Get in Touch
                                </button>
                        </>
                    )}
                </span>
            </div>
            <div className="flex items-center gap-3">
                <span className="text-[13px] text-white/90 whitespace-nowrap">
                    {!isExpired && usageHigh && (
                        <span className="font-semibold text-white">{usagePct}% used</span>
                    )}
                    {!isExpired && usageHigh && daysLow && (
                        <span className="mx-2 text-white/50">·</span>
                    )}
                    {!isExpired && daysLow && (
                        <span className="font-semibold text-white">{dayLabel}</span>
                    )}
                </span>
                {/* <button
                    onClick={handleDismiss}
                    className="text-white/70 hover:text-white transition-colors flex-shrink-0"
                    aria-label="Dismiss banner"
                >
                    <XIcon className="w-4 h-4" />
                </button> */}
            </div>
        </div>
    );
}
